import React, { useState, useMemo, useEffect } from 'react';
import { PrioritySubTabs, SearchBar, Pagination } from './SharedComponents';
import { 
  useGetPriceHistoryVendorsQuery, 
  useUpdateVendorPriorityPriceMutation,
  useGetPriceHistoryCategoriesQuery, 
  useUpdateCategoryPriorityPriceMutation,
  useRecalculateProductPriorityPointsMutation 
} from '../../../../../reducers/priceHistoryApi';
import { useGetSettingByKeyQuery, useUpsertSettingMutation } from '../../../../../reducers/settingsApi';
import TableLoader from '../../../../../components/TableSkelton/TableLoader';

const Stepper = ({ value, onChange = () => {}, isLoading = false }) => (
  <div className="d-flex align-items-center gap-3">
    <button 
      type="button"
      className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center" 
      style={{ width: '24px', height: '24px', border: '1px solid #ced4da', backgroundColor: 'white', color: '#6c757d' }}
      onClick={(e) => {
        e.preventDefault();
        onChange(parseInt(value || 0, 10) - 1);
      }}
      disabled={isLoading}
    >
      <span style={{ fontSize: '18px', lineHeight: '0' }}>&mdash;</span>
    </button>
    <span style={{ fontWeight: '600', minWidth: '15px', textAlign: 'center' }}>{value}</span>
    <button 
      type="button"
      className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center" 
      style={{ width: '24px', height: '24px', border: '1px solid #ced4da', backgroundColor: 'white', color: '#6c757d' }}
      onClick={(e) => {
        e.preventDefault();
        onChange(parseInt(value || 0, 10) + 1);
      }}
      disabled={isLoading}
    >
      <span style={{ fontSize: '18px', lineHeight: '0' }}>+</span>
    </button>
  </div>
);

const PriorityVariables = () => {
  const [activeSubTab, setActiveSubTab] = useState('brand');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    setSearchTerm('');
    setCurrentPage(1);
  }, [activeSubTab]);

  const { data: vendorsData, isLoading: isVendorsLoading } = useGetPriceHistoryVendorsQuery(undefined, {
    skip: activeSubTab !== 'brand'
  });
  const [updateVendorPriority, { isLoading: isVendorUpdating }] = useUpdateVendorPriorityPriceMutation();

  const { data: categoriesData, isLoading: isCategoriesLoading } = useGetPriceHistoryCategoriesQuery(undefined, {
    skip: activeSubTab !== 'category'
  });
  const [updateCategoryPriority, { isLoading: isCategoryUpdating }] = useUpdateCategoryPriorityPriceMutation();

  const { data: settingsResponse, isLoading: isSettingsLoading } = useGetSettingByKeyQuery('app.pricinghistory');
  const [upsertSetting, { isLoading: isSettingUpdating }] = useUpsertSettingMutation();

  const rawSettings = useMemo(() => {
    if (!settingsResponse?.success || !settingsResponse?.data?.value) return null;
    let value = settingsResponse.data.value;
    if (typeof value === 'string') {
      try { value = JSON.parse(value); } catch (e) { return null; }
    }
    return value;
  }, [settingsResponse]);

  const pricePriorities = rawSettings?.pricePriority || { high: 1000, mid: 500, low: 250 };
  const salePriorities = rawSettings?.matric || { onSale: 3 };

  const [recalculatePoints, { isLoading: isRecalculating }] = useRecalculateProductPriorityPointsMutation();

  const handleVendorUpdate = async (vendorId, newValue) => {
    if (newValue < 0) return;
    try {
      await updateVendorPriority({ vendorId, priorityPoint: newValue }).unwrap();
    } catch (error) {
      console.error('Failed to update vendor priority:', error);
    }
  };

  const handleCategoryUpdate = async (categoryId, newValue) => {
    if (newValue < 0) return;
    try {
      await updateCategoryPriority({ categoryId, categoryType: 'subcategory', priorityPoint: newValue }).unwrap();
    } catch (error) {
      console.error('Failed to update category priority:', error);
    }
  };

  const handleSettingUpdate = async (section, key, newValue) => {
    if (!rawSettings || newValue < 0) return;

    try {
        const updatedSection = { ...(rawSettings[section] || {}) };
        updatedSection[key] = newValue;

        const newSettings = { 
            ...rawSettings,
            [section]: updatedSection 
        };
        
        // Ensure structure preservation
        if(!newSettings.veriables) newSettings.veriables = {};
        if(!newSettings.pricePriority) newSettings.pricePriority = {};
        if(!newSettings.matric) newSettings.matric = {};

        const payload = {
          key: 'app.pricinghistory',
          value: newSettings, 
          description: settingsResponse.data.description || "Sundial Pricing History",
          category: settingsResponse.data.category || "app"
        };
        
        await upsertSetting(payload).unwrap();
    } catch (error) {
        console.error('Failed to update settings:', error);
    }
  };
  
  const handleRecalculate = async () => {
    try {
        await recalculatePoints().unwrap();
        // Ideally show a success message here
    } catch (error) {
        console.error('Failed to recalculate points:', error);
    }
  };

  const processedData = useMemo(() => {
    let data = [];
    if (activeSubTab === 'brand') {
       data = vendorsData?.data || [];
       if (searchTerm) {
         data = data.filter(v => (v.fullName || v._id).toLowerCase().includes(searchTerm.toLowerCase()));
       }
    } else if (activeSubTab === 'category') {
       data = categoriesData?.data || [];
       if (searchTerm) {
        data = data.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
       }
    } else if (activeSubTab === 'price') {
       data = [
         { id: 'high', label: 'High', threshold: pricePriorities.high, points: 3 },
         { id: 'mid', label: 'Mid', threshold: pricePriorities.mid, points: 2 },
         { id: 'low', label: 'Low', threshold: pricePriorities.low, points: 1 }
       ];
       if (searchTerm) {
        data = data.filter(i => i.label.toLowerCase().includes(searchTerm.toLowerCase()));
       }
    } else if (activeSubTab === 'sale') {
       data = [
         { id: 'onSale', label: 'On Sale', points: salePriorities.onSale }
       ];
       if (searchTerm) {
        data = data.filter(i => i.label.toLowerCase().includes(searchTerm.toLowerCase()));
       }
    }
    return data;
  }, [activeSubTab, vendorsData, categoriesData, pricePriorities, salePriorities, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, currentPage]);


  const renderContent = () => {
    // Show loader if main data is loading
    if ((activeSubTab === 'brand' && isVendorsLoading) ||
        (activeSubTab === 'category' && isCategoriesLoading) ||
        ((activeSubTab === 'price' || activeSubTab === 'sale') && isSettingsLoading)) {
      return <TableLoader rowsNum={5} cellNum={2} />;
    }

    // Common Table Header/Body Structure varies slightly, so we keep switch for rendering tables only
    switch (activeSubTab) {
      case 'brand':
        return (
          <>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Brand</th>
                  <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Priority Points</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((vendor) => (
                  <tr key={vendor._id}>
                    <td className="align-middle">{vendor.fullName || vendor._id}</td>
                    <td className="align-middle">
                      <Stepper 
                        value={vendor.priorityPoint || 0} 
                        onChange={(val) => handleVendorUpdate(vendor._id, val)}
                        isLoading={isVendorUpdating}
                      />
                    </td>
                  </tr>
                ))}
                {paginatedData.length === 0 && <tr><td colSpan="2">No brands found</td></tr>}
              </tbody>
            </table>
          </div>
          <Pagination 
             currentPage={currentPage}
             totalItems={processedData.length}
             pageSize={pageSize}
             onPageChange={setCurrentPage}
          />
          </>
        );
      case 'category':
        return (
          <>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Category</th>
                  <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Priority Points</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((cat) => (
                  <tr key={cat._id}>
                    <td className="align-middle">{cat.name}</td>
                    <td className="align-middle">
                      <Stepper 
                        value={cat.priorityPoint || 0} 
                        onChange={(val) => handleCategoryUpdate(cat._id, val)}
                        isLoading={isCategoryUpdating}
                      />
                    </td>
                  </tr>
                ))}
                {paginatedData.length === 0 && <tr><td colSpan="2">No categories found</td></tr>}
              </tbody>
            </table>
          </div>
          <Pagination 
             currentPage={currentPage}
             totalItems={processedData.length}
             pageSize={pageSize}
             onPageChange={setCurrentPage}
          />
          </>
        );
      case 'price':
        return (
          <>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Priority</th>
                  <th style={{ borderTop: 'none', fontWeight: 'bold' }}>If cost is greater than</th>
                  <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Priority Points</th>
                </tr>
              </thead>
              <tbody>
                
                {paginatedData.map((item) => (
                    <tr key={item.id}>
                    <td className="align-middle">{item.label}</td>
                    <td className="align-middle">
                        <Stepper 
                            value={item.threshold} 
                            onChange={(val) => handleSettingUpdate('pricePriority', item.id, val)}
                            isLoading={isSettingUpdating}
                        />
                    </td>
                    <td className="align-middle"><Stepper value={item.points} isLoading={true} /></td>
                    </tr>
                ))}
                 {paginatedData.length === 0 && <tr><td colSpan="3">No records found</td></tr>}
              </tbody>
            </table>
          </div>
           <Pagination 
             currentPage={currentPage}
             totalItems={processedData.length}
             pageSize={pageSize}
             onPageChange={setCurrentPage}
          />
          </>
        );
      case 'sale':
        return (
          <>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Metric</th>
                  <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Sale Priority Variables</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                    <tr key={item.id}>
                    <td className="align-middle">{item.label}</td>
                    <td className="align-middle">
                        <Stepper 
                            value={item.points} 
                            onChange={(val) => handleSettingUpdate('matric', item.id, val)}
                            isLoading={isSettingUpdating}
                        />
                    </td>
                    </tr>
                ))}
                {paginatedData.length === 0 && <tr><td colSpan="2">No records found</td></tr>}
              </tbody>
            </table>
          </div>
          <Pagination 
             currentPage={currentPage}
             totalItems={processedData.length}
             pageSize={pageSize}
             onPageChange={setCurrentPage}
          />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-4 bg-white">
      <div className="d-flex justify-content-between align-items-center mb-3">
          <PrioritySubTabs activeSubTab={activeSubTab} onSubTabChange={setActiveSubTab} />
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={handleRecalculate}
            disabled={isRecalculating}
          >
            {isRecalculating ? 'Recalculating...' : 'Recalculate Priorities'}
          </button>
      </div>

      <SearchBar 
        value={searchTerm} 
        onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }} 
        placeholder={`Search ${activeSubTab}...`}
      />
      
      <div className="mt-4">
        {renderContent()}
      </div>
    </div>
  );
};

export default PriorityVariables;
